// Voice and Transcriber Language Compatibility Utils

export interface CompatibilityCheck {
  isCompatible: boolean;
  message?: string;
  suggestedVoices?: string[];
  suggestedTranscriberLanguage?: string;
}

// Arabic voices that only work with Arabic transcriber
export const ARABIC_ONLY_VOICES = ['Natalie', 'MonaArabic', 'Khaled'];

// Arabic transcriber language that only works with Arabic voices
export const ARABIC_TRANSCRIBER_LANGUAGE = 'ar-AE';

/**
 * Check if a voice is compatible with a transcriber language
 */
export const checkVoiceTranscriberCompatibility = (
  voiceId: string,
  _voiceLanguageCode: string, // Currently not used in logic but kept for API consistency
  transcriberLanguage: string
): CompatibilityCheck => {
  const isArabicVoice = ARABIC_ONLY_VOICES.includes(voiceId);
  const isArabicTranscriber = transcriberLanguage === ARABIC_TRANSCRIBER_LANGUAGE;

  // Case 1: Arabic voice with non-Arabic transcriber
  if (isArabicVoice && !isArabicTranscriber) {
    return {
      isCompatible: false,
      message: `The voice "${voiceId}" only works with Arabic transcriber language. Please select "Arabic" as the transcriber language.`,
      suggestedTranscriberLanguage: ARABIC_TRANSCRIBER_LANGUAGE
    };
  }

  // Case 2: Arabic transcriber with non-Arabic voice
  if (isArabicTranscriber && !isArabicVoice) {
    return {
      isCompatible: false,
      message: `Arabic transcriber language only works with Arabic voices (${ARABIC_ONLY_VOICES.join(' or ')}). Please select a compatible voice.`,
      suggestedVoices: ARABIC_ONLY_VOICES
    };
  }

  // Case 3: Compatible combination
  return {
    isCompatible: true
  };
};

/**
 * Validate the complete assistant configuration for voice-transcriber compatibility
 */
export const validateAssistantConfiguration = (assistant: {
  voice?: { voiceId?: string; language?: string };
  transcriber?: { language?: string };
}): CompatibilityCheck => {
  const voiceId = assistant.voice?.voiceId;
  const voiceLanguage = assistant.voice?.language;
  const transcriberLanguage = assistant.transcriber?.language;

  if (!voiceId || !transcriberLanguage) {
    return { isCompatible: true }; // Skip validation if missing data
  }

  return checkVoiceTranscriberCompatibility(voiceId, voiceLanguage || '', transcriberLanguage);
};